<template>
  <!-- Table -->
  <b-card>
    <template #header>
      <h4 class="card-title">
        Payloads
      </h4>
    </template>
    <FilterTimewindow
      :is-cumulative="true"
      default-timewindow="day"
      :timeranges="['custom']"
      @change="timewindowHandler"
    />
    <!-- config -->
    <b-row
      no-gutters
      class="mb-2"
    >
      <b-col lg="3">
        <div class="input-group">
          <div class="input-group-prepend w-75">
            <span
              for="per-page-select"
              class="input-group-text text-black bg-transparent"
            >Items Per Page</span>
          </div>
          <b-form-select
            id="per-page-select"
            v-model="perPagePayload"
            class="bg-transparent w-25"
            style="min-width: 60px"
            :options="[
              { value: 10, text: '10' },
              { value: 20, text: '20' },
              { value: 50, text: '50' },
              { value: 100, text: '100' },
            ]"
          />
        </div>
      </b-col>
      <b-col lg="7" />
      <!-- Export button -->
      <b-col
        lg
        class="d-flex align-items-end mt-3 mt-lg-0"
      >
        <div class="w-100 flex-shrink-0">
          <DownloadDataButton
            :data="csvData"
            :is-enabled="(deviceUplinkMeta.current_page == deviceUplinkMeta.last_page) && !isLoading && !!csvData.length"
            :file-name="csvFileName"
            :fields="fields"
          />
        </div>
      </b-col>
    </b-row>
    <div v-if="!isLoading">
      <b-row
        no-gutters
        class="my-0 mx-0"
      >
        <!-- Table -->
        <b-table
          :items="payloads"
          :fields="fields"
          :busy="isLoading"
          responsive
          hover
          striped
          class="mt-0"
          show-empty
          empty-text="There are no payloads to show. Consider changing the uplink date."
          :current-page="currentPagePayload"
          :per-page="perPagePayload"
        >
          <template #head()="data">
            <span class="text-info text-capitalize">{{ formatLabel(data.label) }}</span>
          </template>
        </b-table>
      </b-row>
      <b-row
        no-gutters
        class="my-0 mx-0"
      >
        <b-col>
          <b-pagination
            v-model="currentPagePayload"
            limit="5"
            align="fill"
            size="md"
            :total-rows="payloads.length"
            :per-page="perPagePayload"
          />
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <Skeleton
        class="mt-4"
        height="600px"
      />
    </div>
  </b-card>
</template>

<script>
import { format } from 'date-fns';
import FilterTimewindow from '@/components/FilterTimewindow';
import DownloadDataButton from '@/components/DownloadDataButton';
import Skeleton from '@/components/Skeleton';

export default {
  components: {
    FilterTimewindow,
    DownloadDataButton,
    Skeleton,
  },
  props: {
    deviceDetail: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      timewindow: {},
      deviceUplinks: [],
      deviceUplinkMeta: {},
      isLoading: false,
      perPagePayload: 50,
      currentPagePayload: 1,
      hiddenKeys: [
        'Active_Energy_Demand_to_Grid_Debug',
        'Active_Energy_Demand_to_Load_Debug',
        'Reactive_Energy_Demand_to_Load_Debug',
        'Active_Energy_Supply_to_Grid_Debug',
        'Active_Energy_Supply_to_Load_Debug',
        'Reactive_Energy_Supply_to_Load_Debug',
      ],
    };
  },
  computed: {
    // mapping deviceUplinks.payloads
    payloads() {
      let finalPayloads = [];
      this.deviceUplinks.forEach((deviceUplinks) => {
        finalPayloads = finalPayloads.concat(deviceUplinks.payloads);
      });
      return finalPayloads;
    },
    // fields will map the keys from deviceDetail.latest_payload
    fields() {
      const fields = [
        {
          key: 'datetime',
          label: 'Datetime',
          formatter: (value) => format(new Date(value), 'dd-MM-yyyy HH:mm:ss'),
          thClass: 'custom-th-datetime',
          sortable: true,
        },
      ];
      if (this.deviceDetail.latest_payload) {
        // push object keys to fields but filter the hidden keys
        Object.keys(this.deviceDetail.latest_payload).forEach((key) => {
          if (!this.hiddenKeys.includes(key)) {
            fields.push({
              key,
              label: key,
            });
          }
        });
      }
      return fields;
    },
    timeWindowDefault() {
      return {
        start: new Date(),
        end: new Date(),
      };
    },
    csvData() {
      const csvData = [];
      this.payloads.forEach((payload) => {
        const csvDataItem = {};
        Object.keys(payload).forEach((key) => {
          csvDataItem[key] = payload[key];
        });
        csvData.push(csvDataItem);
      });
      return csvData;
    },
    csvFileName() {
      const start = format(new Date(this.timewindow.start), 'yyyy-MM-dd');
      const end = format(new Date(this.timewindow.end), 'yyyy-MM-dd');
      const number = this.deviceDetail.device_id;
      return `Device Payloads - ${number} - ${start}_${end}`;
    },
    formattedFields() {
      return this.fields.map((field) => ({
        ...field,
        label: this.formatLabel(field.label),
      }));
    },
  },
  watch: {
    deviceDetail: {
      deep: true,
      immediate: true,
      handler() {
        this.timewindowHandler(this.timeWindowDefault);
      },
    },
  },
  methods: {
    format,

    async getDeviceUplinks(start_date, end_date) {
      try {
        this.isLoading = true;
        const { data } = await this.$api.getDeviceUplinks(
          {
            device_id: this.deviceDetail.device_id,
            start_date,
            end_date,
            load_payloads: 1,
            per_page: 100,
          },
        );
        this.deviceUplinks = data.data;
        this.deviceUplinkMeta = data.meta;
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },
    timewindowHandler(timewindow) {
      this.timewindow = timewindow;
      // change params start_date and end_date format it to year-month-day
      const start_date = format(new Date(timewindow.start), 'yyyy-MM-dd');
      const end_date = format(new Date(timewindow.end), 'yyyy-MM-dd');
      if (this.deviceDetail.device_id) {
        this.getDeviceUplinks(start_date, end_date);
      }
    },
    formatLabel(label) {
      return label.replace(/_/g, ' ');
    },
  },
};
</script>
<style lang="scss" scoped>
</style>
